import { NavigateFunction } from 'react-router-dom';

import { CreateServiceFormValues } from './types';
import { FormObject, LocationNodeData } from '../../../@types';
import { ServiceStatusCode, ServiceStatusLabel } from '../../../@types/service.type';
import { useAddService } from '../../../queries/service-query';
import { formatDate } from '../../../utils/common';
import { getServiceAttributes } from '../../../utils/services';

export interface SubmitCreateServiceArgs {
  values: CreateServiceFormValues;
  clientId: number;
  contractId: number;
  brandId: number;
  parentId: string;
  brandSelectionId: string;
  mainLocationNode?: LocationNodeData;
  subLocationNode?: LocationNodeData;
  reportedFaultId: string;
}

export const buildServicePayload = (args: SubmitCreateServiceArgs) => {
  const {
    values,
    clientId,
    contractId,
    brandId,
    parentId,
    brandSelectionId,
    mainLocationNode,
    subLocationNode,
    reportedFaultId,
  } = args;

  const attributeSource = {
    referenceNo1: values.referenceNo1,
    referenceNo2: values.referenceNo2,
    referenceNo3: values.referenceNo3,
    reportedSerialNumber: values.reportedSerialNumber,
    reportedSerialNumberPartId: values.reportedSerialNumberPartId ?? 0,
    reportedModel: values.reportedModel,
    reportedFaultAndDescription: reportedFaultId,
  };

  return {
    id: 0,
    serviceStatus: ServiceStatusLabel.New,
    serviceStatusCode: ServiceStatusCode.New,
    serviceTypeCode: values.serviceTypeCode,
    actionTypeCode: values.actionTypeCode,
    processTypeCode: values.processTypeCode,
    clientId,
    contractId,
    brandId,
    clientHierarchy: `[${clientId},${contractId},${brandId}]`,
    clientHierarchyId: brandSelectionId !== '0' ? brandSelectionId : parentId,
    priorityCode: values.priorityCode,
    subLocationNodeId: subLocationNode?.id,
    mainLocationNodeId: mainLocationNode?.id,
    scheduledAt: formatDate(values.scheduledAt, ''),
    notes: values.notes,
    serviceAttributes: getServiceAttributes(attributeSource as never),
    userDepotAllocationNodeId: values.userDepotAllocationNodeId,
    elementForDisplaying: [],
  } as never;
};

export type AddServiceMutation = ReturnType<typeof useAddService>;

export interface PerformSubmitDeps {
  addService: AddServiceMutation;
  navigate: NavigateFunction;
  onSuccessMessage: () => void;
}

export interface SubmitOutcome {
  ok: boolean;
  serviceId?: number;
  hasInteractions: boolean;
}

export const performSubmit = async (
  args: SubmitCreateServiceArgs,
  deps: PerformSubmitDeps
): Promise<SubmitOutcome> => {
  const payload = buildServicePayload(args);
  const result = (await deps.addService.mutateAsync(payload)) as {
    id?: number;
    interactionFormData?: unknown[];
  } | null;
  if (!result) return { ok: false, hasInteractions: false };
  const hasInteractions = !!(result.interactionFormData && result.interactionFormData.length > 0);
  if (hasInteractions) return { ok: false, serviceId: result.id, hasInteractions: true };
  deps.onSuccessMessage();
  if (result.id) deps.navigate(`/services/${result.id}`);
  return { ok: true, serviceId: result.id, hasInteractions: false };
};

export interface ResubmitWithInteractionsArgs {
  originalPayload: unknown;
  forms: FormObject[];
}

export const performInteractionResubmit = async (
  args: ResubmitWithInteractionsArgs,
  deps: PerformSubmitDeps
): Promise<SubmitOutcome> => {
  const payload = {
    ...(args.originalPayload as Record<string, unknown>),
    byPassInteractionCheck: true,
    interactionForms: args.forms,
    actionTriggers: [],
  };
  const result = (await deps.addService.mutateAsync(payload as never)) as {
    id?: number;
    interactionFormData?: unknown[];
  } | null;
  if (!result) return { ok: false, hasInteractions: false };
  // Backend may surface a fresh round of interactions if validation still fails.
  const hasInteractions = !!(result.interactionFormData && result.interactionFormData.length > 0);
  if (hasInteractions) return { ok: false, serviceId: result.id, hasInteractions: true };
  deps.onSuccessMessage();
  if (result.id) deps.navigate(`/services/${result.id}`);
  return { ok: true, serviceId: result.id, hasInteractions: false };
};
